import { makeObservable, observable, override, runInAction, toJS } from 'mobx';

import { ScanType } from 'constants/ScanType';
import { SortDirection } from 'constants/SortDirection';
import {
    buildWhereFilter,
    EntityName,
    get,
    getCount,
    getDetailsForCorrectPagination,
    getPage,
    patch,
    post
} from 'stores/_services/dataService';
import { getConditions, getItemListConditions, getListConditions } from 'stores/_services/filterService';
import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import LocationStore from 'stores/location/LocationStore';
import { IDataFetchOptions, IGetDataParams } from '../../types';

export interface ICreateLocationArgs {
    name: string;
    description: string;
    organizationId: string;
    data: any;
}

const SEARCH_FIELDS = ['name'];

let filterUpdated = true;

export default class LocationListStore extends BaseListStore<LocationStore> {
    filteredCount = 0;

    constructor() {
        super({
            itemStore: LocationStore,
            sortBy: 'name',
            sortDirection: SortDirection.ASC
        });

        makeObservable(this, {
            clearIds: override,
            filteredCount: observable
        });
    }

    async getLocations(
        args: IDataFetchOptions = {},
        filterUpdate: boolean,
        pageNumber = DEFAULT_PAGE_NUMBER,
        pageSize = DEFAULT_LIMIT,
        applyLimit = true
    ): Promise<void> {
        if (!filterUpdated) {
            filterUpdated = filterUpdate;
        }

        const shouldReset = this._updateListState(args);
        const conditions = getConditions(this.filter);

        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                order: this.sort,
                ...(applyLimit && { limit: pageSize }),
                ...(applyLimit && { skip: (pageNumber - 1) * pageSize }),
                where: conditions,
                include: [
                    {
                        relation: 'organization',
                        scope: {
                            fields: ['name', 'data']
                        }
                    }
                ]
            }
        };

        const { data } = await getPage(EntityName.LOCATION_DETAILS, options);
        const totalCount = await getCount(EntityName.LOCATION_DETAILS, { where: conditions, searchFields: SEARCH_FIELDS });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);

        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });

        if (filterUpdated) {
            const filteredCount = await getCount(EntityName.LOCATION_DETAILS, options.filter);
            runInAction(() => {
                this.filteredCount = filteredCount;
                filterUpdated = false;
            });
        }
    }

    async getLocationsForCorrectPagination(args: IGetDataParams = {}): Promise<void> {
        const { filter, sortBy, sortDirection, pageSize, page } = args;

        const include = [
            {
                relation: 'organization',
                scope: {
                    fields: ['name', 'data']
                }
            }
        ];

        const params = {
            filter: filter || this.filter,
            sortBy: sortBy || this.sortBy,
            sortDirection: sortDirection || this.sortDirection,
            pageSize: pageSize || this.pageSize,
            page: page || this.page,
            searchFields: SEARCH_FIELDS,
            include
        };

        const { data, totalCount } = await getDetailsForCorrectPagination<LocationStore>({
            params,
            entityName: EntityName.LOCATION_DETAILS
        });

        runInAction(() => {
            this.filteredCount = totalCount;
        });

        this.updateStateForCorrectPagination<LocationStore>({ args, data, totalCount });
    }

    async getLocationData(id: string): Promise<void> {
        const options = {
            filter: {
                include: [
                    {
                        relation: 'organization',
                        scope: {
                            fields: ['name', 'data']
                        }
                    }
                ]
            }
        };
        const { data } = await get(`${EntityName.LOCATION_DETAILS}/${id}`, options);
        this._updateData(data);
    }

    async existsLocationName(name: string, organizationId: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name, organizationId: organizationId }
            }
        };
        const { data } = await get('locations', options);
        return !!data.length;
    }

    async existsDeviceName(name: string, locationId: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name, locationId: locationId }
            }
        };
        const { data } = await get('agents', options);
        return !!data.length;
    }

    async existsLocationNameInOrganization(name: string, organizationId: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name, organizationId: organizationId }
            }
        };
        const { data } = await get('locations', options);
        return !!data.length;
    }

    async editLocation(id: string, name: string, description: string, organizationId: string, oldName: string): Promise<boolean> {
        const exists = name !== oldName && (await this.existsLocationName(name, organizationId));
        if (exists) {
            return false;
        }
        const payload = {
            ...(name !== oldName ? { name: name } : {}),
            description: description
        };
        await patch(`locations/${id}`, payload);
        await this.getLocations({ reset: true }, true);
        return true;
    }

    async addLocation({ name, description, organizationId, data }: ICreateLocationArgs): Promise<boolean> {
        const exists = await this.existsLocationName(name, organizationId);
        if (exists) {
            return false;
        }
        const payload = {
            name: name,
            description: description,
            organizationId: organizationId,
            data
        };
        const { result } = await post('locations', payload);

        if (result) {
            return result;
        }

        return true;
    }

    async moveLocations({
                            idsList,
                            organizationId
                        }: {
        idsList: string[],
        organizationId: string
    }): Promise<void> {
        const payload = {
            organizationId,
            idsList
        };
        await post('locations/moveLocations', payload);
        this.selectedIds = {};
        await this.getLocations({ reset: true }, true);
    }

    async deleteLocations(idsList: string[]): Promise<void> {
        const payload = { idsList };

        await post('locations/deleteLocations', payload);
        this.selectedIds = {};
        await this.getLocations({ reset: true }, true);
    }

    async addDevice(name: string, locationId: string): Promise<boolean> {
        const exists = await this.existsDeviceName(name, locationId);
        if (exists) {
            return false;
        }
        const payload = {
            name: name,
            locationId: locationId
        };
        await post('agents', payload);
        await this.getLocations({ reset: true }, true);
        return true;
    }

    async assignPolicy(policyId: string, locationId: string, deviceGroupId: string): Promise<void> {
        const payload = {
            filter: {
                where: { id: policyId }
            },
            locationId,
            deviceGroupId
        };

        await post(`policies/assignToLocation`, payload);
        await this.getLocations({ reset: true }, true);
    }

    async assignPolicies(policyId: string, deviceGroupId: string): Promise<void> {
        const conditions = getListConditions(this.selectedIdList, this.filter);

        const where = buildWhereFilter({
            searchFields: SEARCH_FIELDS,
            where: conditions
        });

        const payload = {
            locationsFilter: { where: where },
            policyIds: [policyId],
            deviceGroupId
        };

        await post('locations/assignPolicies', payload);
        await this.getLocations({ reset: true }, true);
    }

    async scan({
        locationId,
        scanType,
        options,
        ids,
        filter
    }: {
        locationId?: string;
        scanType: ScanType;
        options: any;
        ids?: string[];
        filter?: any;
    }): Promise<void> {
        let conditions: any[] = [];

        if (scanType === ScanType.FILTERED) {
            conditions = getConditions(filter);
        } else if (scanType === ScanType.SELECTED) {
            if (this.selectedIdList && this.selectedIdList.length) {
                conditions = getItemListConditions(this.selectedIdList);
            }
        }

        if (ids && ids.length) {
            conditions.push({
                id: { inq: ids }
            });
        }

        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        if (options?.extensions) {
            options.extensions = toJS(options.extensions).map((i: any, idx: number) => ({
                id: i.id,
                name: i.name,
                order: idx
            }));
        }

        await post(`locations/${locationId}/scan`, { where, options });
    }

    async scanLocations({
        locationIds,
        scanType,
        options,
        filter,
        organizationId
    }: {
        locationIds: string[];
        scanType: ScanType;
        options: any;
        filter?: any;
        organizationId?: string;
    }): Promise<void> {
        let conditions: any[] = [];
        if (scanType === ScanType.FILTERED) {
            conditions = getConditions(filter);
        }
        if (locationIds && locationIds.length) {
            conditions.push({
                id: locationIds
            });
        }

        if (organizationId) {
            conditions.push({
                organizationId: organizationId
            });
        }

        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });
        if (options?.extensions) {
            options.extensions = toJS(options.extensions).map((i: any, idx: number) => ({
                id: i.id,
                name: i.name,
                order: idx
            }));
        }
        await post('locations/scan', { where, options });
    }

    async getAccessibleAgentCount(ids: Array<string>, locationId?: string): Promise<void> {
        const conditions = getConditions(this.filter);
        conditions.push({ active: true });

        if (locationId) {
            conditions.push({ locationId });
        }

        if (ids && ids.length) {
            conditions.push({
                agentId: { inq: ids }
            });
        }

        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

    }

    async getActiveHostAddressCount(locationId: string): Promise<number> {
        const params = {
            where: {
                active: true,
                locationId
            }
        };

        return await getCount('agents', params);
    }
}
